import { chunk, range } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import { TabView } from 'react-native-tab-view';
import {
    DotNavigation,
    Icon,
    type IconType,
    SPACE_ICONS,
    View,
    XStack,
    YStack,
} from 'smoothie';

const PAGE_SIZE = 12;

interface IconSelectionProps {
    onSelect: (icon: string) => void;
    selectedIcon: string;
}

const IconSelection = ({ onSelect, selectedIcon }: IconSelectionProps) => {
    const [index, setIndex] = useState(0);

    // Array mapping index to set of icons
    let iconsToRender = Object.keys(SPACE_ICONS);
    if (iconsToRender.length === 25) {
        // telephone and tv icons are the least used icons; we don't want to have a 3rd icon page during icon selection
        const iconToRemove = selectedIcon === 'telephone' ? 'tv' : 'telephone';
        iconsToRender = iconsToRender.filter((x) => x !== iconToRemove);
    }
    const iconPages = chunk(iconsToRender, PAGE_SIZE);
    const routes = range(iconPages.length).map((i) => ({ key: i.toString() }));

    const IconList = ({ page }: { page: number }) => {
        const icons = iconPages[page];
        return (
            <XStack
                alignItems="center"
                justifyContent="center"
                flexWrap="wrap"
                gap={0}
                rowGap={0}
            >
                {icons?.map((icon, i) => (
                    <IconButton
                        key={i}
                        icon={icon}
                        isSelected={selectedIcon === icon}
                        onPress={() => {
                            onSelect(icon);
                        }}
                    />
                ))}
            </XStack>
        );
    };

    // TabView requires pure components for renderScene mapping -- providing a dynamic function requires
    // recreating the component and re-mounting, which leads to a bad experience
    // So the following is hard-coded to work for 2 pages of icons
    const renderScene = ({ route }: { route: { key: string } }) => {
        switch (route.key) {
            case '0':
                return <IconList page={0} />;
            case '1':
                return <IconList page={1} />;
            default:
                return null;
        }
    };

    return (
        <YStack width="100%" marginBottom={24} alignItems="center" height={300}>
            <DotNavigation
                total={iconPages.length}
                currentIndex={index}
                onSelectIndex={(i) => {
                    setIndex(i);
                }}
            />
            <TabView
                navigationState={{ index, routes }}
                onIndexChange={setIndex}
                renderScene={renderScene}
                renderTabBar={() => null}
                initialLayout={{
                    height: 280,
                    width: 380,
                }}
                style={{
                    height: 280,
                    width: 380,
                }}
            />
        </YStack>
    );
};

interface IconSelectProps {
    icon: string;
    isSelected: boolean;
    onPress: () => void;
}

const IconButton = ({ icon, isSelected, onPress }: IconSelectProps) => {
    return (
        <View
            margin={isSelected ? 5 : 6} // prevent shifting caused by larger selected icon border
            padding={24}
            borderRadius={12}
            backgroundColor="$white"
            borderWidth={isSelected ? 2 : 1}
            borderColor={isSelected ? '$black' : '$gray400'}
            onPress={onPress}
            cursor="pointer"
        >
            <Icon
                icon={SPACE_ICONS[icon] as IconType}
                size={24}
                color={isSelected ? '$black' : '$gray1000'}
            />
        </View>
    );
};

export default IconSelection;
